import { Constants } from '@constants';
import {
    GetAccountsResponse,
    GetFormalOffersResponse
} from '@iwoca/lapi-client/edge';
import { RootState } from '@iwProvider/configureStore';
import { differenceInDays } from 'date-fns';
import _ from 'lodash';

import { hasOpenApplication } from './dashboardWidgets/tabularFlexiDashboard/multiLoanDashboardSelector';
import {
    getApplicationStatus,
    APPLICATION_STATUS_DECLINED
} from '../../api/lendingAPI/ApplicationSelector';
import { getCountry } from '../../iwIntlProvider/CountryProvider';
import { TProductType } from '../dashboard/Dashboard.types';
import { isFlexi24Duration } from '../flexi24/getIsFlexi24Duration';
import { FormalOffer } from '../myOffers/MyOffers.types';

export const OUTSTANDING_STATUS = 'ongoing';
export const OVERDUE_STATUS = 'overdue';
export const AWAITING_FUNDS_STATUS = 'pending';
export const TOPUP_STATUS = 'topup';
export const ENDED_STATUS = 'ended';
export const UNKNOWN_STATUS = 'unknown'; // this is either Approved not taken, approval expired, declined or deferred (see https://iwoca.atlassian.net/browse/CBIL-151)

type TFormalOffers = NonNullable<
    GetFormalOffersResponse['data']
>['formal_offers'];
type TAccount = NonNullable<GetAccountsResponse['data']>['accounts'][number];
export const getProductTypeFromOfferId = (
    { formal_offers }: { formal_offers: TFormalOffers },
    offerId: string
) => {
    const formalOffer = formal_offers.find(
        (offer) => offer.offer_id === offerId
    );
    return formalOffer!.product_type;
};

function getBalance(state: RootState, productType?: TProductType) {
    const accountWithProductType = getAccountByProductType(state, productType);
    return accountWithProductType?.balance;
}

export function getOutstandingTotal(
    state: RootState,
    productType?: TProductType
) {
    return getBalance(state, productType)?.total;
}

export function getAccountId(state: RootState, productType: TProductType) {
    return getAccountByProductType(state, productType)?.account_id;
}

export function getOutstandingPrincipal(
    state: RootState,
    productType?: TProductType
) {
    return getBalance(state, productType)?.principal;
}

export function getOutstandingInterest(
    state: RootState,
    productType: TProductType
) {
    return getBalance(state, productType)?.interest;
}

const getTimeOutDate = (state: RootState, productType: TProductType) => {
    const formalOfferTimeOut = getLatestFormalOfferOfTypeFromAccounts(
        state,
        productType
    )?.timeout;
    if (formalOfferTimeOut) {
        return new Date(formalOfferTimeOut).toISOString();
    }
};

const getAccountLoanStatus = (state: RootState, productType: TProductType) => {
    return getAccountByProductType(state, productType)?.loan_status;
};

export function hasApprovalExpired(
    state: RootState,
    productType: TProductType
) {
    const timeOutDate = getTimeOutDate(state, productType);
    return differenceInDays(new Date(timeOutDate!), new Date()) < 0;
}

export function getOutstandingFees(
    state: RootState,
    productType: TProductType
) {
    return (
        getBalance(state, productType)?.other_fees +
        getBalance(state, productType)?.product_fees
    );
}

export function getNextRepaymentAmount(
    state: RootState,
    productType: TProductType
) {
    return getRepaymentsSchedule(state, productType)?.[0]?.total;
}

export function getNextRepaymentDate(
    state: RootState,
    productType: TProductType
) {
    return getRepaymentsSchedule(state, productType)?.[0]?.date;
}

function getRepaymentsSchedule(state: RootState, productType: TProductType) {
    return getScheduledPayments(state, productType)?.schedule;
}

export function getMaxMonthlyInterestRate(
    state: RootState,
    productType: TProductType
) {
    const rates =
        getScheduledPayments(state, productType)?.summary?.rate_structures?.[0]
            ?.rates ?? [];
    const maximumPrincipleRate = _.maxBy(rates, 'max_principal');
    // @ts-expect-error This is likely a bug
    return maximumPrincipleRate?.rate!;
}

export function getScheduledPayments(
    state: RootState,
    productType: TProductType
) {
    const accountOfProductType = getAccountByProductType(state, productType);
    return accountOfProductType?.scheduled_payments;
}

export const FLEXI_LOAN_PRODUCT_TYPE = 'credit_facility';
export const FLEXI_24_PRODUCT_TYPE = 'flexi24';
export const REVENUE_BASED_LOAN_PRODUCT_TYPE = 'merchant_cash_advance';
export const BUSINESS_LOAN_PRODUCT_TYPE = 'term_loan';
export const IWOCAPAY_PRODUCT_TYPE = 'iwocapay';
export const CBILS_PRODUCT_TYPE = 'cbils';

function getNonCBILSAccountProductType(state: RootState) {
    return getAccountByProductType(state)?.product_type;
}

export function getAccountByProductType(
    state: RootState,
    productType?: TProductType
) {
    if (!productType)
        return _.find(
            getAllAccounts(state),
            ({ product_type }) => product_type !== CBILS_PRODUCT_TYPE
        );

    const account = _.find(
        getAllAccounts(state),
        ({ product_type }) => product_type === productType
    );
    if (productType === FLEXI_24_PRODUCT_TYPE && !account) {
        return _.find(
            getAllAccounts(state),
            (account) =>
                account.product_type === FLEXI_LOAN_PRODUCT_TYPE &&
                isFlexi24Duration(account?.duration)
        );
    }

    return account;
}

export function isPrimaryAccountCBILS(state: RootState) {
    const primaryAccountProductType =
        state?.api?.accounts?.data?.accounts?.[0]?.product_type;
    return primaryAccountProductType === CBILS_PRODUCT_TYPE;
}

function getCreditLimit(state: RootState, productType?: TProductType) {
    const accountWithProductType = getAccountByProductType(state, productType);
    return accountWithProductType?.max_approved_loan_amount;
}

export function getAllAccounts(state: RootState) {
    return state?.api?.accounts?.data?.accounts ?? [];
}

function getTopUpEligibility(state: RootState, productType?: TProductType) {
    return getAccountByProductType(state, productType)?.topup_eligibility;
}

export function isTopUpEligible(state: RootState, productType: TProductType) {
    const isTopupAllowed =
        getTopUpEligibility(state, productType)?.is_allowed ?? false;
    if (productType) {
        return isTopupAllowed;
    }

    return (
        getNonCBILSAccountProductType(state) !== FLEXI_24_PRODUCT_TYPE &&
        isTopupAllowed
    );
}

export function getMaxOutstandingPrincipalBeforeTopUpAllowed(
    state: RootState,
    productType: TProductType
) {
    return (
        getTopUpEligibility(state, productType)
            ?.max_outstanding_principal_before_topup ?? false
    );
}

export function amountToRepayBeforeTopUp(
    state: RootState,
    productType?: TProductType
) {
    return getTopUpEligibility(state, productType)
        ?.amount_to_repay_before_topup;
}

export function getLatestFormalOfferFromAccounts(state: RootState) {
    return getAccountWithLatestFormalOffer(state)?.latest_formal_offer;
}

const TWELVE_MONTHS_INTEREST_FREE_PROMOTION = 'twelve_months_interest_free';
export function getInterestRateFromCBILSAccount(cbilsAccount: TAccount) {
    const interestRates = cbilsAccount?.interest_rates;
    const hasTwelveMonthsInterestFree = !!_.find(
        cbilsAccount?.promotions ?? [],
        (promotion) => promotion === TWELVE_MONTHS_INTEREST_FREE_PROMOTION
    );
    const today = new Date();
    const loanAgreementDate = new Date(interestRates?.[0]?.from_date!);
    const timeoutOfTwelveMonthInterestFree = loanAgreementDate.setFullYear(
        loanAgreementDate.getFullYear() + 1
    );
    let activeRate;

    if (
        hasTwelveMonthsInterestFree &&
        // @ts-expect-error
        today < timeoutOfTwelveMonthInterestFree
    ) {
        activeRate = _.find(
            interestRates,
            ({ from_date }) =>
                new Date(from_date!).setHours(23, 59, 59, 999) >
                timeoutOfTwelveMonthInterestFree
        );
    } else {
        activeRate = _.findLast(
            interestRates,
            // @ts-expect-error
            ({ from_date }) => today > new Date(from_date).setHours(0, 0, 0, 0)
        );
    }

    return activeRate?.rate;
}

function getMaxCashAmount(state: RootState, productType: TProductType) {
    return (
        getLatestFormalOfferOfTypeFromAccounts(state, productType)
            ?.available_cash ?? 0
    );
}

const hasExpiredApproval = (state: RootState, productType: TProductType) => {
    const latestFormalOffer = getLatestFormalOfferOfTypeFromAccounts(
        state,
        productType
    );
    const latestFormalOfferWithCredit = latestFormalOffer?.available_credit > 0;

    const timeoutDate = latestFormalOffer?.timeout;

    if (!timeoutDate && latestFormalOfferWithCredit) {
        return true;
    }

    return differenceInDays(new Date(timeoutDate), new Date()) < 0;
};

const getAccountWithLatestFormalOffer = (state: RootState) => {
    const accounts = state?.api?.accounts?.data?.accounts ?? [];
    const latestFormalOfferAccount = _.find(
        accounts,
        ({ latest_formal_offer }) => latest_formal_offer?.available_credit > 0
    );

    return latestFormalOfferAccount;
};

function getLatestFormalOfferOfTypeFromAccounts(
    state: RootState,
    productType: TProductType
) {
    const accounts = state?.api?.accounts?.data?.accounts;
    const accountWithOffer = _.find(
        accounts,
        (account) => account?.latest_formal_offer?.product_type === productType
    );

    if (productType === FLEXI_24_PRODUCT_TYPE && !accountWithOffer) {
        const flexi24Offer = _.find(
            accounts,
            (account) =>
                account?.latest_formal_offer?.product_type ===
                    FLEXI_LOAN_PRODUCT_TYPE &&
                isFlexi24Duration(account?.latest_formal_offer?.max_duration)
        );
        return flexi24Offer?.latest_formal_offer;
    }

    return accountWithOffer?.latest_formal_offer;
}

export function getDrawnDownLoanAmount(
    state: RootState,
    productType?: TProductType
) {
    return getAccountByProductType(state, productType)?.loan_amount;
}

export function getMaxAmountCreditAvailable(
    state: RootState,
    productType: TProductType
) {
    const creditLimit = getCreditLimit(state, productType);
    const expired = hasExpiredApproval(state, productType);
    const latestFormalOffer = getLatestFormalOfferOfTypeFromAccounts(
        state,
        productType
    );

    if (!expired && latestFormalOffer) {
        return latestFormalOffer.max_amount;
    }
    return creditLimit;
}

export function getAccountByAccountId(state: RootState, accountId: string) {
    return getAllAccounts(state).find(
        (account: TAccount) => account.account_id === accountId
    );
}

export const getLoanDurationByMonth = (
    state: RootState,
    productType: TProductType = FLEXI_LOAN_PRODUCT_TYPE
) => {
    //if loan is not outstanding we use account with latestformalOffer
    const loanStatus = getAccountLoanStatus(state, productType);
    let maxDuration;
    if (loanStatus === OUTSTANDING_STATUS) {
        maxDuration =
            getAccountByProductType(state, productType)?.duration ?? 0;
    } else
        maxDuration =
            getLatestFormalOfferOfTypeFromAccounts(state, productType)
                ?.max_duration ?? 0;

    if (maxDuration.unit === 'days') {
        return getMonthsfromDays(maxDuration.amount);
    }
    return maxDuration.amount;
};

export function getMonthsfromDays(days: number) {
    return Math.floor(days / 30);
}

export const isTopUpPending = (state: RootState, productType: TProductType) => {
    return hasOpenApplication(state, productType);
};

const DEProductTypeConversion = (productType: TProductType) => {
    if (getCountry() !== 'DE') return productType;
    if (productType === FLEXI_24_PRODUCT_TYPE) {
        return FLEXI_LOAN_PRODUCT_TYPE;
    }
    return productType;
};

// We check for > MIN_DRAWDOWN_AMOUNT here for when there is residual available cash which isn't able to be drawn down
// E.g. they have less than £1 or £2 (which was stopping topup applications)
// We chose MIN_DRAWDOWN_AMOUNT as a safeguard to cover all customers since they cant drawn down less than that anyway.
export const hasAvailableCash = (state: RootState, productType: TProductType) =>
    getMaxCashAmount(state, productType) >= Constants.MIN_DRAW_DOWN_AMOUNT;

const canTopUpNow = (state: RootState, productType: TProductType) =>
    isTopUpEligible(state, productType) &&
    amountToRepayBeforeTopUp(state, productType) === 0;

export const canTopUpAfterRepayments = (
    state: RootState,
    productType: TProductType
) =>
    !canTopUpNow(state, productType) &&
    isTopUpEligible(state, productType) &&
    !hasAvailableCash(state, productType);

export const canRequestTopUp = (state: RootState, productType: TProductType) =>
    canTopUpNow(state, productType) &&
    !isTopUpPending(state, productType) &&
    (hasApprovalExpired(state, productType) ||
        !hasAvailableCash(state, productType));

export const isTopUpApproved = (
    state: RootState,
    productType: TProductType
) => {
    productType = DEProductTypeConversion(productType);

    if (hasInterestOnlyExtensionApproval(state, productType)) return false;

    return (
        isLatestFormalOfferValid(state, productType) &&
        !hasApprovalExpired(state, productType) &&
        hasAvailableCash(state, productType)
    );
};

const isLatestFormalOfferValid = (
    state: RootState,
    productType: TProductType
) => {
    return getLatestFormalOfferOfTypeFromAccounts(state, productType)?.is_valid;
};

export const isTopUpDeclined = (state: RootState) =>
    getApplicationStatus(state) === APPLICATION_STATUS_DECLINED;

export const isPreApproved = (state: RootState, productType: TProductType) => {
    const latestFormalOffer = getLatestFormalOfferFromAccounts(state);

    return (
        latestFormalOffer?.is_pre_approval &&
        isTopUpEligible(state, productType) &&
        isTopUpApproved(state, productType)
    );
};

export const hasInterestOnlyExtensionApproval = (
    state: RootState,
    productType: TProductType
): boolean => {
    //Interest Only Extensions are determined by:
    // 1. formal_offer is an interest only offer
    // 2. formal_offer offer_id is different to the offer that's been drawn down on
    // 3. formal_offer is still valid
    const account = getAccountByProductType(state, productType);

    if (account) {
        const currentOffer = account.loan_offer_id;

        const fundedOffer = account.formal_offers.find(
            (offer: FormalOffer) => offer.offer_id === currentOffer
        );

        const expiredOffer = account.formal_offers.find(
            (offer: FormalOffer) => !offer.is_valid
        );

        const interestOnlyOffer = account.formal_offers.find(
            (offer: FormalOffer) =>
                offer.interest_only_repayments &&
                offer.interest_only_repayments === 3
        );

        if (expiredOffer || fundedOffer || !interestOnlyOffer) return false;

        return Boolean(interestOnlyOffer.is_valid);
    }

    return false;
};
